import React, { useState } from 'react';
import { View, StyleSheet, ScrollView, Pressable } from 'react-native';
import {
  Text,
  TextInput,
  useTheme,
  Chip,
  Card,
  IconButton,
} from 'react-native-paper';
import { useFormContext, Controller } from 'react-hook-form';
import {
  Coffee,
  Zap,
  Droplet,
  Wind,
  Flame,
  Scale as ScaleIcon,
  Thermometer as ThermometerIcon,
  Timer as TimerIcon,
  Filter as FilterIcon,
  MoreHorizontal,
  Plus,
  X,
} from 'lucide-react-native';

const equipmentTypes = [
  { value: 'grinder', label: 'Grinder', icon: Coffee },
  { value: 'espresso_machine', label: 'Espresso Machine', icon: Zap },
  { value: 'pour_over', label: 'Pour Over', icon: Droplet },
  { value: 'french_press', label: 'French Press', icon: Wind },
  { value: 'aeropress', label: 'Aeropress', icon: Wind },
  { value: 'moka_pot', label: 'Moka Pot', icon: Flame },
  { value: 'siphon', label: 'Siphon', icon: Flame },
  { value: 'turkish_pot', label: 'Turkish Pot', icon: Flame },
  { value: 'scale', label: 'Scale', icon: ScaleIcon },
  { value: 'kettle', label: 'Kettle', icon: Flame },
  { value: 'filter', label: 'Filter', icon: FilterIcon },
  { value: 'thermometer', label: 'Thermometer', icon: ThermometerIcon },
  { value: 'timer', label: 'Timer', icon: TimerIcon },
  { value: 'other', label: 'Other', icon: MoreHorizontal },
];

export function EquipmentBasicInfo() {
  const theme = useTheme();
  const colors = theme.colors;
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const isDark = theme.dark;
  const tags = watch('tags') || [];
  const [newTag, setNewTag] = useState('');

  const addTag = () => {
    if (newTag.trim()) {
      setValue('tags', [...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const removeTag = (index: number) => {
    setValue(
      'tags',
      tags.filter((_: any, i: number) => i !== index)
    );
  };

  return (
    <View style={styles.container}>
      {/* Name */}
      <View style={styles.fieldContainer}>
        <Text
          variant='labelLarge'
          style={[styles.label, { color: colors.onSurface }]}
        >
          Equipment Name *
        </Text>
        <Controller
          control={control}
          name='name'
          render={({ field: { onChange, onBlur, value } }) => (
            <TextInput
              mode='outlined'
              placeholder='e.g., Baratza Encore'
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              error={!!errors['name']}
              style={styles.input}
              outlineStyle={styles.inputOutline}
            />
          )}
        />
        {errors['name'] && (
          <Text variant='bodySmall' style={styles.errorText}>
            {errors['name'].message as string}
          </Text>
        )}
      </View>

      {/* Brand */}
      <View style={styles.fieldContainer}>
        <Text
          variant='labelLarge'
          style={[styles.label, { color: colors.onSurface }]}
        >
          Brand *
        </Text>
        <Controller
          control={control}
          name='brand'
          render={({ field: { onChange, onBlur, value } }) => (
            <TextInput
              mode='outlined'
              placeholder='e.g., Baratza'
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              error={!!errors['brand']}
              style={styles.input}
              outlineStyle={styles.inputOutline}
            />
          )}
        />
        {errors['brand'] && (
          <Text variant='bodySmall' style={styles.errorText}>
            {errors['brand'].message as string}
          </Text>
        )}
      </View>

      {/* Model */}
      <View style={styles.fieldContainer}>
        <Text
          variant='labelLarge'
          style={[styles.label, { color: colors.onSurface }]}
        >
          Model
        </Text>
        <Controller
          control={control}
          name='model'
          render={({ field: { onChange, onBlur, value } }) => (
            <TextInput
              mode='outlined'
              placeholder='e.g., ESP-001'
              value={value || ''}
              onChangeText={onChange}
              onBlur={onBlur}
              error={!!errors['model']}
              style={styles.input}
              outlineStyle={styles.inputOutline}
            />
          )}
        />
      </View>

      {/* Description */}
      <View style={styles.fieldContainer}>
        <Text
          variant='labelLarge'
          style={[styles.label, { color: colors.onSurface }]}
        >
          Description *
        </Text>
        <Controller
          control={control}
          name='description'
          render={({ field: { onChange, onBlur, value } }) => (
            <TextInput
              mode='outlined'
              placeholder='Describe the equipment, its features, and what makes it special...'
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              error={!!errors['description']}
              multiline
              numberOfLines={6}
              style={[styles.input, styles.textArea]}
              outlineStyle={styles.inputOutline}
            />
          )}
        />
        {errors['description'] && (
          <Text variant='bodySmall' style={styles.errorText}>
            {errors['description'].message as string}
          </Text>
        )}
      </View>

      {/* Equipment Type */}
      <View style={styles.fieldContainer}>
        <Text
          variant='titleMedium'
          style={[styles.label, { color: colors.onSurface }]}
        >
          Equipment Type *
        </Text>
        <Controller
          control={control}
          name='equipment_type'
          render={({ field: { onChange, value } }) => (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              style={styles.chipRow}
              contentContainerStyle={styles.chipRowContent}
            >
              {equipmentTypes.map(type => (
                <View key={type.value} style={styles.chipWrapper}>
                  <Chip
                    selected={value === type.value}
                    onPress={() => onChange(type.value)}
                    style={styles.chip}
                    compact={false}
                    textStyle={styles.chipLabel}
                  >
                    {type.label}
                  </Chip>
                </View>
              ))}
            </ScrollView>
          )}
        />
      </View>

      {/* Tags */}
      <View style={styles.fieldContainer}>
        <Text
          variant='labelLarge'
          style={[styles.label, { color: colors.onSurface }]}
        >
          Tags
        </Text>
        <View style={styles.editorAddRow}>
          <TextInput
            mode='outlined'
            placeholder='Add a tag...'
            value={newTag}
            onChangeText={setNewTag}
            style={[styles.input, { flex: 1 }]}
            outlineStyle={styles.inputOutline}
            onSubmitEditing={addTag}
          />
          <IconButton
            icon={() => <Plus size={20} color={colors.primary} />}
            onPress={addTag}
            mode='contained'
            containerColor={colors.primaryContainer}
            size={20}
          />
        </View>
        <View style={styles.editorChipContainer}>
          {tags.map((tag: string, index: number) => (
            <View
              key={index}
              style={[
                styles.editorChip,
                {
                  backgroundColor: isDark
                    ? 'rgba(103, 80, 164, 0.15)'
                    : 'rgba(103, 80, 164, 0.08)',
                },
              ]}
            >
              <Text
                variant='bodySmall'
                style={{ color: colors.primary, flex: 1 }}
              >
                {tag}
              </Text>
              <Pressable onPress={() => removeTag(index)}>
                <X size={14} color={colors.primary} />
              </Pressable>
            </View>
          ))}
        </View>
      </View>

      {/* Tips */}
      <Card
        style={[
          styles.card,
          styles.tipsCard,
          { backgroundColor: theme.colors.primaryContainer },
        ]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={[
              styles.tipsTitle,
              { color: theme.colors.onPrimaryContainer },
            ]}
          >
            Tips for Basic Info
          </Text>
          <Text
            variant='bodySmall'
            style={[
              styles.tipsText,
              { color: theme.colors.onPrimaryContainer },
            ]}
          >
            • Use clear, descriptive names that include brand and model{'\n'}•
            Write detailed descriptions highlighting key features{'\n'}• Choose
            the most specific equipment type{'\n'}• Add relevant tags to improve
            discoverability
          </Text>
        </Card.Content>
      </Card>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 20,
  },
  fieldContainer: {
    gap: 8,
  },
  label: {
    fontWeight: '600',
  },
  helperText: {
    fontSize: 12,
    marginTop: -4,
  },
  editorAddRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  editorChipContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  editorChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
  },
  input: {
    fontSize: 15,
  },
  inputOutline: {
    borderRadius: 12,
  },
  textArea: {
    minHeight: 120,
    textAlignVertical: 'top',
    paddingTop: 12,
  },
  errorText: {
    color: '#F44336',
    marginTop: 4,
  },
  chipRow: {
    marginBottom: 16,
  },
  chipRowContent: {
    paddingRight: 8,
    gap: 8,
  },
  chipWrapper: {
    flexShrink: 0,
  },
  chip: {
    height: 40,
    borderRadius: 20,
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chipLabel: {
    fontSize: 14,
    lineHeight: 20,
  },
  card: {
    marginTop: 8,
  },
  tipsCard: {
    marginTop: 8,
  },
  tipsTitle: {
    marginBottom: 8,
    fontWeight: 'bold',
  },
  tipsText: {
    lineHeight: 20,
  },
});
